import React, { useState, useRef } from "react";
import { successMsg } from "../SuccessHandler";
import Swal from "sweetalert2";
import ProfilesTable from "../tables/ProfilesTable";
import TracksByProfileTable from "../tables/TracksByProfileTable";
import CopyProfileModal from "./CopyProfileModal";
import LastChangesModal from "../lastChanges/LastChangesModal";
import ErrorCodes from "../../constants/ErrorCodes";
import SiteConfig from "../../constants/SiteConfig";
import { useInsertFunctions } from "../../functions/InsertFunctions";
import Rights from "../../constants/Rights";

const TrackByProfileModal = ({
  data,
  profiles,
  gameProviders,
  setSelectedProvider,
  selectedProvider,
  getTracksByProfile,
  idFirstProfile,
  tracksByProfile,
  setTracksByProfile,
  getProfiles,
  setRefresh,
  profileOptionSelected,
  setProfileOptionSelected,
  lastChanges,
  setLastChanges,
  lastChangesInfo,
  setLastChangesInfo,
  fieldChangesInfo,
  setFieldChangesInfo,
  getFieldChanges,
  openLastChanges,
  profileName,
  setProfileName,
  setSessionNoLongerExists,
  publicIP,
  setOpenByProfileModal,
  getTracksListCbx,
  tracksListCbx,
}) => {
  const [filterInput, setFilterInput] = useState("");
  const [selectedRow, setSelectedRow] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [uiTypeSelected, setUiTypeSelected] = useState(0);
  const closeButtonRef = useRef();
  const divRef = useRef(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { insertTracksByProfile } = useInsertFunctions();
  const userRightsList = JSON.parse(localStorage.getItem("UserRights")) || [];
  const [isCopyOpen, setIsCopyOpen] = useState(false);

  //OnChange Tracks by Profile Table
  const tracksByProfileChangeHandler = (trackID) => {
    setTracksByProfile((prevTracksByProfile) => {
      const trackIndex = prevTracksByProfile.some(
        (track) =>
          track.TrackID === trackID &&
          track.UIType === parseInt(uiTypeSelected),
      );

      if (trackIndex) {
        // The trackID with the UIType already exists and must be deleted.
        return prevTracksByProfile.filter(
          (track) =>
            track.TrackID !== trackID ||
            track.UIType !== parseInt(uiTypeSelected),
        );
      } else {
        // The trackID with the UIType does not exist; it must be added.
        return [
          ...prevTracksByProfile,
          {
            TrackID: trackID,
            UIType: parseInt(uiTypeSelected),
          },
        ];
      }
    });
    setHasChanges(true);
  };

  //Insert Tracks by Profile Function
  const handleInsertTracksByProfile = async (
    tracksByProfile,
    close,
    profile,
  ) => {
    if (hasChanges) {
      const res = await insertTracksByProfile(
        {
          ProfileID: idFirstProfile,
          LastModification: new Date().toISOString(),
          ProfileTracksList: tracksByProfile,
          PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
          IP: publicIP,
        },
        data.LogonSessionID,
      );

      if (res?.error) {
        if (
          res.Result === ErrorCodes.InvalidSession ||
          res.Result === ErrorCodes.SessionNoLongerExists
        ) {
          setSessionNoLongerExists(true);
        }
        return;
      } else {
        successMsg("Save Tracks By Profile");
        setHasChanges(false);

        //Update the status only if the insert is not done from the profiles table.
        if (!profile) {
          setTracksByProfile(tracksByProfile);
        }

        if (close) {
          closeButtonRef.current.click();
          setTracksByProfile([]);
          setSelectedRow(0);
          setProfileOptionSelected("");
          setUiTypeSelected(0);
          setOpenByProfileModal(false);
        }
      }
    }
  };

  // This function is executed when closing the modal from the close button or from x
  const closeModal = () => {
    if (hasChanges) {
      Swal.fire({
        title: "Are you sure?",
        text: "Changes will be saved!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Exit anyway",
        confirmButtonText: "Yes, save it!",
        allowOutsideClick: false,
      }).then((result) => {
        if (result.value) {
          handleInsertTracksByProfile(tracksByProfile, true, false);
          setRefresh(false);
          setSelectedProvider(-1);
          setUiTypeSelected(0);
          setFilterInput("");
          setSelectAll(false);
        } else {
          closeButtonRef.current.click();
          const div = divRef.current;
          if (div) {
            div.scrollTop = 0;
          }
          setHasChanges(false);
          setTracksByProfile([]);
          setSelectedRow(0);
          setProfileOptionSelected("");
          setRefresh(false);
          setSelectedProvider(-1);
          setUiTypeSelected(0);
          setFilterInput("");
          setSelectAll(false);
          setOpenByProfileModal(false);
        }
      });
    } else {
      closeButtonRef.current.click();
      const div = divRef.current;
      if (div) {
        div.scrollTop = 0;
      }
      setHasChanges(false);
      setTracksByProfile([]);
      setSelectedRow(0);
      setProfileOptionSelected("");
      setRefresh(false);
      setSelectedProvider(-1);
      setUiTypeSelected(0);
      setFilterInput("");
      setSelectAll(false);
      setOpenByProfileModal(false);
    }
  };

  // This function is executed when user clicks on Refresh button
  const refreshTrackByProfiles = () => {
    getProfiles();
    getTracksByProfile(profileOptionSelected);
    setRefresh(true);
  };

  return (
    <>
      <div
        className="modal fade"
        id="trackByProfileModal"
        role="dialog"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header headerModals">
              <h5
                className="modal-title"
                id="trackByProfileModal"
                style={{ cursor: "default" }}
              >
                Tracks by Profile
              </h5>
              <span
                data-dismiss="modal"
                ref={closeButtonRef}
                style={{ cursor: "inherit" }}
              ></span>
              <button type="button" className="close" onClick={closeModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-4">
                  <ProfilesTable
                    profiles={profiles}
                    filterInput={filterInput}
                    setFilterInput={setFilterInput}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    hasChanges={hasChanges}
                    setHasChanges={setHasChanges}
                    getTracksByProfile={getTracksByProfile}
                    handleInsertTracksByProfile={handleInsertTracksByProfile}
                    tracksByProfile={tracksByProfile}
                    setProfileOptionSelected={setProfileOptionSelected}
                    uiTypeSelected={uiTypeSelected}
                    setUiTypeSelected={setUiTypeSelected}
                    setProfileName={setProfileName}
                    setSelectAll={setSelectAll}
                    setTracksByProfile={setTracksByProfile}
                  />
                </div>
                <div className="col-sm-8">
                  <TracksByProfileTable
                    gameProviders={gameProviders}
                    setSelectedProvider={setSelectedProvider}
                    selectedProvider={selectedProvider}
                    trackList={tracksListCbx}
                    tracksByProfile={tracksByProfile}
                    tracksByProfileChangeHandler={tracksByProfileChangeHandler}
                    uiTypeSelected={uiTypeSelected}
                    setUiTypeSelected={setUiTypeSelected}
                    profileOptionSelected={profileOptionSelected}
                    setTracksByProfile={setTracksByProfile}
                    setHasChanges={setHasChanges}
                    selectAll={selectAll}
                    setSelectAll={setSelectAll}
                    getTracksListCbx={getTracksListCbx}
                    selectedRow={selectedRow}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="col-sm-8">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => refreshTrackByProfiles()}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setIsCopyOpen(true)}
                >
                  Copy
                </button>
                {userRightsList.find(
                  (right) => right.FormName === Rights.FRM_LAST_CHANGES,
                ) && (
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={() => {
                      openLastChanges(
                        "PROFILETRACKENABLELOG",
                        profileOptionSelected,
                        0,
                        profileName,
                      );
                      setIsModalOpen(true);
                    }}
                  >
                    Last Changes
                  </button>
                )}
              </div>
              <div
                className="col-sm-4"
                style={{ textAlign: "right", paddingRight: "0px" }}
              >
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() =>
                    handleInsertTracksByProfile(tracksByProfile, false, false)
                  }
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isCopyOpen && (
        <CopyProfileModal
          isOpen={isCopyOpen}
          onClose={() => setIsCopyOpen(false)}
          data={data}
          profiles={profiles}
          profileOptionSelected={profileOptionSelected}
          setSessionNoLongerExists={setSessionNoLongerExists}
          publicIP={publicIP}
        />
      )}
      {isModalOpen && (
        <LastChangesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          lastChanges={lastChanges}
          setLastChanges={setLastChanges}
          lastChangesInfo={lastChangesInfo}
          getFieldChanges={getFieldChanges}
          fieldChangesInfo={fieldChangesInfo}
          setLastChangesInfo={setLastChangesInfo}
          setFieldChangesInfo={setFieldChangesInfo}
        />
      )}
    </>
  );
};

export default TrackByProfileModal;
